import rawAxios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios'
import { AuthInactivityMonitor } from 'features/auth/AuthInactivityMonitor'
import { GeckoAuthenticationContext } from 'features/auth/geckoAuthContext'
import { attachAccessPreviewMutationGuard } from 'features/permissions/utils/accessPreviewMutationGuard'
import { clearAccessPreviewFromStorage } from 'features/permissions/utils/accessPreviewStorage'
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useCognito } from './CognitoProvider'

/**
 * A component which handles Cognito authentication, providing a GeckoAuthContext
 * to the rest of the app
 * @param children        The rest of the app (this component is a simple wrapper)
 */
export const CognitoAuthContext: FC<{
  children: ReactNode
}> = ({ children }) => {
  const cognito = useCognito()
  const [authorization, setAuthorization] = useState<string>()
  const [validRefreshToken, setValidRefreshToken] = useState<boolean>(true)
  const [tokenExpiration, setTokenExpiration] = useState<Date>()

  // we do this so the linter doesn't get mad at us that `cognito` is not included in dependency array
  // we can't include it in the array because the cognito object changes on access token refresh
  // which causes the effect starting the modal countdown timer to run twice
  const exchangeRefreshTokenAlias = cognito.exchangeRefreshToken
  const attemptGetAccessToken = useCallback(() => {
    const tokenExchangePromise = exchangeRefreshTokenAlias()
    if (tokenExchangePromise) {
      // if the promise resolves, the token exchange was successful and we don't need to do anything
      // the logic for updating the cognito user is handled by the provider
      tokenExchangePromise.catch(() => {
        setValidRefreshToken(false)
      })
    }
  }, [exchangeRefreshTokenAlias])

  useEffect(() => {
    if (!cognito.user?.access_token || !cognito.user.expires_at) {
      throw new Error('Cognito user requires access_token and expires_at')
    }
    setAuthorization(`Bearer ${cognito.user.access_token}`)
    setValidRefreshToken(true)
    setTokenExpiration(new Date(cognito.user.expires_at * 1000))
  }, [cognito, cognito.user])

  const authorizedAxios = useMemo(() => {
    const newAxios = rawAxios.create()
    attachAccessPreviewMutationGuard(newAxios)
    newAxios.interceptors.request.use((requestConfig: AxiosRequestConfig) => {
      return {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          Authorization: authorization ?? null,
        } as AxiosRequestHeaders,
      }
    })
    return newAxios
  }, [authorization])

  const logout = React.useCallback(() => {
    clearAccessPreviewFromStorage()
    cognito.signOut()
  }, [cognito])

  return (
    <>
      <AuthInactivityMonitor
        tokenExpiration={tokenExpiration}
        validRefreshToken={validRefreshToken}
        attemptGetAccessToken={attemptGetAccessToken}
        // SPLAT-498: this should eventually switch to sendToCognitoLogin
        redirectToLogin={logout}
      />
      <GeckoAuthenticationContext.Provider
        value={{
          authorization: authorization,
          authorizedAxios,
          isAuthenticated: !!authorization,
          logout: logout,
        }}>
        {children}
      </GeckoAuthenticationContext.Provider>
    </>
  )
}
